// İSG uzmanı portalı — Risk Değerlendirme **meta** düzenleme formu (Modal + RHF + Zod).
// api-contract §19.1: `PUT /risk-assessments/{uuid}` (yalnız meta: başlık/saha/alan/tarih/
// sonraki gözden geçirme/notlar). Tehlike kalemleri PUT ile değişmez — detay ekranı §19.3
// uçlarıyla yönetir. Metodoloji değiştirilemez (istekte yok).
//
// **Düzenlenebilirlik (§19).** Bu modal yalnız düzenlenebilir değerlendirmede açılır (detay
// düzenle butonu aksi hâlde gizli) → BE `409 RISK_ASSESSMENT_NOT_EDITABLE` UI'da önlenir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  type RiskAssessmentDto,
  Select,
  type SelectOption,
  Spinner,
  resolveApiErrorMessage,
  riskApi,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { riskKeys } from '../../lib/riskKeys';

const OPTIONS_PAGE_SIZE = 200;

interface RiskAssessmentFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessment: RiskAssessmentDto;
}

/** UTC ISO → `yyyy-MM-dd` (date input; §4 sunum). */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `yyyy-MM-dd` (yerel gün başlangıcı) → UTC ISO wire (§4); boş → null. */
function dateToIso(dateStr: string): string | null {
  if (!dateStr) {
    return null;
  }
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function RiskAssessmentFormModal({
  open,
  onOpenChange,
  assessment,
}: RiskAssessmentFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const sitesQuery = useQuery({
    queryKey: ['risk', 'options', 'sites'],
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open,
    staleTime: 60_000,
  });

  const siteOptions: SelectOption[] = useMemo(
    () =>
      (sitesQuery.data?.items ?? []).map((s) => ({
        value: s.uuid,
        label: s.branchName ? `${s.name} · ${s.branchName}` : s.name,
      })),
    [sitesQuery.data],
  );

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().trim().min(1, t('risk.editor.validation.titleRequired')),
        siteUuid: z.string().min(1, t('risk.editor.validation.siteRequired')),
        area: z.string(),
        assessmentDate: z.string().min(1, t('risk.editor.validation.assessmentDateRequired')),
        nextReviewDate: z.string(),
        notes: z.string(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      title: assessment.title,
      siteUuid: assessment.siteUuid,
      area: assessment.area ?? '',
      assessmentDate: toDateInput(assessment.assessmentDate),
      nextReviewDate: toDateInput(assessment.nextReviewDate),
      notes: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      riskApi.updateRiskAssessment(assessment.uuid, {
        title: values.title.trim(),
        siteUuid: values.siteUuid,
        area: values.area.trim().length > 0 ? values.area.trim() : null,
        assessmentDate: dateToIso(values.assessmentDate) ?? assessment.assessmentDate,
        nextReviewDate: dateToIso(values.nextReviewDate),
        notes: values.notes.trim().length > 0 ? values.notes.trim() : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.detail(assessment.uuid) });
      queryClient.invalidateQueries({ queryKey: riskKeys.list() });
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
        }
        onOpenChange(next);
      }}
      title={t('risk.form.edit.title')}
      description={t('risk.form.description')}
      size="lg"
      closeLabel={t('risk.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('risk.common.cancel')}
          </Button>
          <Button type="submit" form="risk-meta-form" loading={mutation.isPending}>
            {mutation.isPending ? <Spinner label={t('risk.common.save')} /> : t('risk.common.save')}
          </Button>
        </>
      }
    >
      <form
        id="risk-meta-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}
        <Field
          htmlFor="risk-edit-title"
          label={t('risk.editor.field.title')}
          error={formState.errors.title?.message}
          required
        >
          <Input
            id="risk-edit-title"
            invalid={Boolean(formState.errors.title)}
            {...register('title')}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            htmlFor="risk-edit-site"
            label={t('risk.editor.field.site')}
            error={formState.errors.siteUuid?.message}
            required
          >
            <Select
              id="risk-edit-site"
              invalid={Boolean(formState.errors.siteUuid)}
              disabled={sitesQuery.isPending}
              placeholder={t('risk.editor.sitePlaceholder')}
              options={siteOptions}
              {...register('siteUuid')}
            />
          </Field>
          <Field htmlFor="risk-edit-area" label={t('risk.editor.field.area')}>
            <Input id="risk-edit-area" {...register('area')} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            htmlFor="risk-edit-date"
            label={t('risk.editor.field.assessmentDate')}
            error={formState.errors.assessmentDate?.message}
            required
          >
            <Input
              id="risk-edit-date"
              type="date"
              invalid={Boolean(formState.errors.assessmentDate)}
              {...register('assessmentDate')}
            />
          </Field>
          <Field
            htmlFor="risk-edit-next-review"
            label={t('risk.editor.field.nextReviewDate')}
            hint={t('risk.editor.field.nextReviewDateHint')}
          >
            <Input id="risk-edit-next-review" type="date" {...register('nextReviewDate')} />
          </Field>
        </div>
        <Field htmlFor="risk-edit-notes" label={t('risk.editor.field.notes')}>
          <Input id="risk-edit-notes" {...register('notes')} />
        </Field>
      </form>
    </Modal>
  );
}
